/**
 * Non-URL UI state (zustand): theme, rail / side panel, viewport class,
 * last visited job, keyboard help overlay, the server's last auto bucket.
 * Persisted bits go through localStorage.
 */
import { create } from "zustand";
import type { ReactNode } from "react";
import { loadLocal, saveLocal } from "@/lib/storage";

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

/** Below this viewport width the rail becomes a drawer (spec §13 phone layout).
 *  The `narrow:` CSS variant in src/styles/index.css uses the same width. */
export const NARROW_QUERY = "(max-width: 899px)";

export interface SidePanelContent {
  title: string;
  node: ReactNode;
  /** stable key so re-opening the same thing does not flicker */
  key?: string;
}

interface UiState {
  themeMode: ThemeMode;
  theme: ResolvedTheme;
  setThemeMode: (m: ThemeMode) => void;
  /** called by ThemeProvider when the OS preference changes */
  applySystemTheme: (dark: boolean) => void;

  /** viewport narrower than NARROW_QUERY (phone / small tablet) */
  narrow: boolean;
  setNarrow: (v: boolean) => void;

  /** desktop: rail collapsed to an icon strip (persisted) */
  railCollapsed: boolean;
  /** narrow: rail drawer open (transient, never persisted) */
  railOpen: boolean;
  /** collapses/expands at desktop widths, opens/closes the drawer when narrow */
  toggleRail: () => void;
  setRailOpen: (v: boolean) => void;
  /** rail: the "not syncing" token group is expanded (persisted, collapsed by default) */
  stoppedJobsOpen: boolean;
  toggleStoppedJobs: () => void;
  /** rail tree: the tokens the reader opened by hand, by job id (persisted; only
   *  `true` entries). The token being viewed is open regardless and folds again
   *  once left unless it is in here (lib/tree `tokenTree`). */
  railTree: Record<string, boolean>;
  setRailTokenOpen: (jobId: string, open: boolean) => void;

  resetJobState: () => void;
  sidePanel: SidePanelContent | null;
  sidePanelOpen: boolean;
  openSidePanel: (c: SidePanelContent) => void;
  closeSidePanel: () => void;
  toggleSidePanel: () => void;

  lastJob: string | null;
  setLastJob: (id: string) => void;

  /** the jump palette (components/CommandPalette) is open */
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (v: boolean) => void;

  searchOpen: boolean;
  setSearchOpen: (v: boolean) => void;

  /** `meta.bucket_secs` of the most recent bucketed response (what "auto" resolved to) */
  serverBucket: number | null;
  setServerBucket: (secs: number | null) => void;
}

function systemDark(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-color-scheme: dark)").matches;
}

function isNarrow(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia(NARROW_QUERY).matches;
}

function resolve(mode: ThemeMode, dark = systemDark()): ResolvedTheme {
  return mode === "system" ? (dark ? "dark" : "light") : mode;
}

/**
 * Write `data-theme` synchronously so anything reading CSS variables right
 * after a toggle (EChart theme registration, canvas palettes) sees the new
 * theme before React re-renders.
 */
function applyThemeAttr(theme: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  if (document.documentElement.dataset.theme !== theme) document.documentElement.dataset.theme = theme;
}

/**
 * The tokens opened by hand. Earlier builds also stored `false` for a token
 * the reader closed; closed is now the default, so those entries are dropped,
 * and every stored open one counts as opened by hand.
 */
function loadRailTree(): Record<string, boolean> {
  const stored = loadLocal<Record<string, boolean>>("rail.tree", {});
  return Object.fromEntries(Object.entries(stored).filter(([, open]) => open === true));
}

export const useUi = create<UiState>((set, get) => {
  const themeMode = loadLocal<ThemeMode>("theme", "system");
  const theme = resolve(themeMode);
  applyThemeAttr(theme);
  return {
    themeMode,
    theme,
    setThemeMode: (m) => {
      saveLocal("theme", m);
      const t = resolve(m);
      applyThemeAttr(t);
      set({ themeMode: m, theme: t });
    },
    applySystemTheme: (dark) => {
      if (get().themeMode !== "system") return;
      const t: ResolvedTheme = dark ? "dark" : "light";
      applyThemeAttr(t);
      set({ theme: t });
    },

    narrow: isNarrow(),
    setNarrow: (v) => {
      if (get().narrow === v) return;
      // leaving the narrow layout: the drawer state is meaningless on desktop
      set({ narrow: v, railOpen: false });
    },

    railCollapsed: loadLocal("rail.collapsed", false),
    railOpen: false,
    toggleRail: () => {
      if (get().narrow) {
        set({ railOpen: !get().railOpen });
        return;
      }
      const v = !get().railCollapsed;
      saveLocal("rail.collapsed", v);
      set({ railCollapsed: v });
    },
    setRailOpen: (v) => set({ railOpen: v }),
    stoppedJobsOpen: loadLocal("rail.stopped.open", false),
    toggleStoppedJobs: () => {
      const v = !get().stoppedJobsOpen;
      saveLocal("rail.stopped.open", v);
      set({ stoppedJobsOpen: v });
    },
    railTree: loadRailTree(),
    setRailTokenOpen: (jobId, open) => {
      const v = { ...get().railTree };
      if (open) v[jobId] = true;
      else delete v[jobId];
      saveLocal("rail.tree", v);
      set({ railTree: v });
    },

    resetJobState: () => set({ sidePanel: null, sidePanelOpen: false, serverBucket: null }),
    sidePanel: null,
    sidePanelOpen: loadLocal("side.open", false),
    openSidePanel: (c) => {
      saveLocal("side.open", true);
      set({ sidePanel: c, sidePanelOpen: true });
    },
    closeSidePanel: () => {
      saveLocal("side.open", false);
      set({ sidePanelOpen: false });
    },
    toggleSidePanel: () => {
      const v = !get().sidePanelOpen;
      saveLocal("side.open", v);
      set({ sidePanelOpen: v });
    },

    lastJob: loadLocal<string | null>("lastJob", null),
    setLastJob: (id) => {
      if (get().lastJob === id) return;
      saveLocal("lastJob", id);
      set({ lastJob: id });
    },

    paletteOpen: false,
    setPaletteOpen: (v) => set({ paletteOpen: v }),
    shortcutsOpen: false,
    setShortcutsOpen: (v) => set({ shortcutsOpen: v }),

    searchOpen: false,
    setSearchOpen: (v) => set({ searchOpen: v }),

    serverBucket: null,
    setServerBucket: (secs) => {
      const v = typeof secs === "number" && Number.isFinite(secs) && secs > 0 ? secs : null;
      if (get().serverBucket === v) return;
      set({ serverBucket: v });
    },
  };
});
